import { OPTIMIZATION_GOAL_TO_KENT_CONSOLIDATION_CONFIG } from '@cast/one-click-features-evictor';
import type {
  ContinuousRebalancingConfig,
  KentContinuousRebalancingFeatureConfig,
  KentCustomSelector,
} from '@cast/types-kent';
import type {
  EvictorAdvancedEvictionConfigEntry,
  EvictorConfig,
} from '@cast/types-workload-eviction';

import { OPTIMIZATION_GOAL_TO_AUTOSCALER_EVICTOR_CONFIG } from '../../_constants';
import { OptimizationGoal } from '../../open-api-types/autopilot-types';

// Structural shape shared by Kent custom selectors and autoscaler eviction config entries
// Defined manually because the platforms' generated settings differ slightly
type NamespacedEvictionEntry = {
  podSelector?: {
    namespace?: string;
  };
  settings?: {
    removalDisabled?: { enabled?: boolean };
  };
};

const mergeExcludedNamespaceEntries = <T extends NamespacedEvictionEntry>({
  existingEntries,
  excludedNamespaces,
  mapExcludedNamespacesToEntries,
}: {
  existingEntries: T[];
  excludedNamespaces: string[];
  mapExcludedNamespacesToEntries: (excludedNamespaces: string[]) => T[];
}): T[] => {
  const excludedNamespacesSet = new Set(excludedNamespaces);

  const preservedEntries = existingEntries.filter((entry) => {
    const isExcludedNamespace =
      entry.podSelector?.namespace &&
      excludedNamespacesSet.has(entry.podSelector.namespace);

    const isRemovalDisabled = entry.settings?.removalDisabled?.enabled === true;

    if (isExcludedNamespace && isRemovalDisabled) {
      return false;
    }

    return true;
  });

  return [
    ...preservedEntries,
    ...mapExcludedNamespacesToEntries(excludedNamespaces),
  ];
};

const mapExcludedNamespacesToCustomSelectors = (
  excludedNamespaces: string[]
): KentCustomSelector[] =>
  excludedNamespaces.map((namespace) => ({
    podSelector: { namespace },
    settings: {
      removalDisabled: { enabled: true },
    },
  }));

const mergeCustomSelectors = ({
  existingSelectors,
  excludedNamespaces,
}: {
  existingSelectors: KentCustomSelector[];
  excludedNamespaces: string[];
}): KentCustomSelector[] =>
  mergeExcludedNamespaceEntries({
    existingEntries: existingSelectors,
    excludedNamespaces,
    mapExcludedNamespacesToEntries: mapExcludedNamespacesToCustomSelectors,
  });

const mapExcludedNamespacesToEvictionConfigEntries = (
  excludedNamespaces: string[]
): EvictorAdvancedEvictionConfigEntry[] =>
  excludedNamespaces.map((namespace) => ({
    podSelector: { namespace },
    settings: {
      removalDisabled: { enabled: true },
    },
  }));

export const mergeAutoscalerEvictionConfigEntries = ({
  existingEntries,
  excludedNamespaces,
}: {
  existingEntries: EvictorAdvancedEvictionConfigEntry[];
  excludedNamespaces: string[];
}): EvictorAdvancedEvictionConfigEntry[] =>
  mergeExcludedNamespaceEntries({
    existingEntries,
    excludedNamespaces,
    mapExcludedNamespacesToEntries:
      mapExcludedNamespacesToEvictionConfigEntries,
  });

export const buildAutoscalerEvictorConfig = (
  optimizationGoal: OptimizationGoal
): EvictorConfig => ({
  ...OPTIMIZATION_GOAL_TO_AUTOSCALER_EVICTOR_CONFIG[optimizationGoal],
  enabled: true,
});

export const mergeKentConsolidationConfig = ({
  optimizationGoal,
  excludedNamespaces,
  existingConfig,
}: {
  optimizationGoal: OptimizationGoal;
  excludedNamespaces: string[];
  existingConfig?: KentContinuousRebalancingFeatureConfig;
}): ContinuousRebalancingConfig => {
  const preset =
    OPTIMIZATION_GOAL_TO_KENT_CONSOLIDATION_CONFIG[optimizationGoal];

  return {
    ...preset,
    enabled: true,
    evictionConfig: {
      customSelectors: mergeCustomSelectors({
        existingSelectors:
          existingConfig?.config?.evictionConfig?.customSelectors ?? [],
        excludedNamespaces,
      }),
    },
  };
};
